import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';

interface Pack {
  id: string;
  version: string;
  name: string;
  description: string;
  requires: string[];
  courses: number;
  labs: number;
  outcomes: number;
}

interface Installed {
  packId: string;
  version: string;
  installedAt: string;
}

/**
 * Curriculum packs.
 *
 * This is the answer to "how do you onboard a ministry": one install, not a
 * migration. A pack brings the curriculum outcomes, a course and a lab, and
 * installing it into a fresh school tenant gives that school something a
 * teacher can open the same afternoon.
 *
 * Re-installing is safe and says so on the button, because the alternative is
 * an administrator who is afraid to click it and a support ticket asking
 * whether it will duplicate everything.
 */
export function PacksPage() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);

  const packs = useQuery({
    queryKey: ['packs'],
    queryFn: () => apiClient.get<Pack[]>('/v1/packs'),
  });

  const installed = useQuery({
    queryKey: ['packs-installed'],
    queryFn: () => apiClient.get<Installed[]>('/v1/packs/installed'),
  });

  const install = useMutation({
    mutationFn: (packId: string) =>
      apiClient.post<Record<string, unknown>>(`/v1/packs/${packId}/install`),
    onSuccess: data => {
      setResult(summarise(data));
      void queryClient.invalidateQueries();
    },
    onError: error => setResult((error as Error).message),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">Curriculum packs</h1>
      <p className="text-sm text-ink-600">
        A pack is data with an id: outcomes, courses and laboratories, installed into this
        organisation. Installing twice changes nothing that is already there.
      </p>

      {result && <p className="mt-3 rounded-lg bg-ink-100 p-3 text-sm text-ink-700">{result}</p>}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {(packs.data ?? []).map(pack => {
          const already = installed.data?.find(row => row.packId === pack.id);
          return (
            <div key={pack.id} className="card flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-base font-semibold text-ink-900">{pack.name}</h2>
                <span className="badge bg-ink-200 text-ink-700">v{pack.version}</span>
              </div>
              <p className="text-sm text-ink-600">{pack.description}</p>
              <p className="text-xs text-ink-500">
                {pack.outcomes} outcomes · {pack.courses} course{pack.courses === 1 ? '' : 's'} ·{' '}
                {pack.labs} laborator{pack.labs === 1 ? 'y' : 'ies'}
              </p>

              {already && (
                <p className="text-xs text-emerald-700">
                  Installed {new Date(already.installedAt).toLocaleDateString()} (v{already.version})
                </p>
              )}

              <button
                type="button"
                className={already ? 'btn-secondary mt-auto' : 'btn-primary mt-auto'}
                disabled={install.isPending}
                onClick={() => install.mutate(pack.id)}
              >
                {install.isPending
                  ? 'Installing…'
                  : already
                    ? 'Re-install (safe)'
                    : 'Install into this organisation'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function summarise(data: Record<string, any>): string {
  const parts = [
    `${data.coursesCreated?.length ?? 0} course(s) created`,
    `${data.coursesUpdated?.length ?? 0} updated`,
    `${data.lessonsCreated ?? 0} lesson(s) created`,
    `${data.labsCreated?.length ?? 0} lab(s) created`,
    `${(data.outcomesCreated ?? 0) + (data.outcomesUpdated ?? 0)} outcome(s)`,
  ];
  return `${data.reinstall ? 'Re-installed' : 'Installed'} ${data.pack}@${data.version} — ${parts.join(', ')}.`;
}
